import { formatCopy } from '@/i18n/ns';
import { overlaysCopy, type OverlaysCopyKey } from '@/i18n/ns/overlays';
import type { AgentSchedule } from './api';

// Schedule presets (PR 4 §7): Off, Every hour, Every day at, Weekdays at,
// Weekly on, Custom cron. The browser turns a preset into a 5-field cron
// expression; the server validates cron. Reading a stored schedule back into
// a preset parses only the cron strings these presets write.

export type SchedulePreset = 'off' | 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'custom';
export interface ScheduleForm {
  preset: SchedulePreset;
  /** HH:MM, 24-hour. */
  time: string;
  /** 0 = Sunday … 6 = Saturday (cron's numbering). */
  weekday: number;
  cron: string;
  timezone: string;
}

export const SCHEDULE_PRESETS: { value: SchedulePreset; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'hourly', label: 'Every hour' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'custom', label: 'Custom' },
];
export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Stockholm';
  } catch {
    return 'Europe/Stockholm';
  }
}

function clockParts(time: string) {
  const [hours, minutes] = time.split(':').map((part) => Number(part));
  const valid =
    Number.isInteger(hours) &&
    Number.isInteger(minutes) &&
    hours >= 0 &&
    hours < 24 &&
    minutes >= 0 &&
    minutes < 60;
  return valid ? { hours, minutes } : { hours: 8, minutes: 0 };
}
const pad = (value: number) => String(value).padStart(2, '0');
const isNumber = (value: string) => value !== '' && String(Number(value)) === value;

/** The cron a preset means, or null for Off. */
export function presetCron(form: ScheduleForm): string | null {
  const { hours, minutes } = clockParts(form.time);
  if (form.preset === 'off') return null;
  if (form.preset === 'hourly') return '0 * * * *';
  if (form.preset === 'daily') return `${minutes} ${hours} * * *`;
  if (form.preset === 'weekdays') return `${minutes} ${hours} * * 1-5`;
  if (form.preset === 'weekly') return `${minutes} ${hours} * * ${form.weekday}`;
  return form.cron.trim() || null;
}

export function formSchedule(form: ScheduleForm): { cron: string; timezone: string } | null {
  const cron = presetCron(form);
  return cron ? { cron, timezone: form.timezone || browserTimezone() } : null;
}

/** The preset a stored schedule came from (Custom when it matches none). */
export function scheduleForm(schedule: AgentSchedule | null | undefined): ScheduleForm {
  const base: ScheduleForm = {
    preset: 'off',
    time: '08:00',
    weekday: 1,
    cron: '',
    timezone: schedule?.timezone ?? browserTimezone(),
  };
  if (!schedule) return base;
  const fields = schedule.cron.trim().split(/\s+/u);
  const custom = { ...base, preset: 'custom' as const, cron: schedule.cron };
  if (fields.length !== 5) return custom;
  const [minute, hour, day, month, weekday] = fields;
  if (schedule.cron.trim() === '0 * * * *') return { ...base, preset: 'hourly' };
  if (!isNumber(minute) || !isNumber(hour) || day !== '*' || month !== '*') return custom;
  const time = `${pad(Number(hour))}:${pad(Number(minute))}`;
  if (weekday === '*') return { ...base, preset: 'daily', time };
  if (weekday === '1-5') return { ...base, preset: 'weekdays', time };
  if (isNumber(weekday) && Number(weekday) <= 6)
    return { ...base, preset: 'weekly', time, weekday: Number(weekday) };
  return custom;
}

/** The overlays copy the schedule words come from; English when a caller has none. */
type ScheduleCopy = Record<OverlaysCopyKey, string>;
const EN = overlaysCopy.en as ScheduleCopy;

/** The weekday's name in the copy's language, in its in-sentence form ("trešdiena", not
 *  "Trešdiena"; "Monday" and "Montag" keep their capitals). Cron numbering: 0 = Sunday. */
function weekdayName(weekday: number, locale: string) {
  // 2023-01-01 was a Sunday; a weekday formatted with a date takes the in-sentence form.
  const day = new Date(Date.UTC(2023, 0, 1 + weekday, 12));
  const parts = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).formatToParts(day);
  return parts.find((part) => part.type === 'weekday')?.value ?? '';
}

export type AgentStatusKind = 'on' | 'off' | 'onDemand' | 'needsYou';

/** An agent's status word (agent redesign 2026-10-09, superseding DES1 WS-01c): On while its
 *  schedule is on, Off while that schedule is off, On demand without a schedule, and Needs you
 *  while its latest run waits for the viewer's approval. */
export function agentStatus(
  hasSchedule: boolean,
  enabled: boolean,
  copy: ScheduleCopy = EN,
  needsYou = false,
) {
  if (needsYou)
    return { kind: 'needsYou' as AgentStatusKind, label: copy.agentStatusNeedsYou, on: false };
  if (!hasSchedule)
    return { kind: 'onDemand' as AgentStatusKind, label: copy.agentStatusOnDemand, on: false };
  return enabled
    ? { kind: 'on' as AgentStatusKind, label: copy.agentStatusOn, on: true }
    : { kind: 'off' as AgentStatusKind, label: copy.agentStatusOff, on: false };
}

/** The schedule line of an agent: "Weekdays at 08:00 · starts when on" while it is off. */
export function scheduleLine(
  schedule: AgentSchedule | null | undefined,
  enabled: boolean,
  copy: ScheduleCopy = EN,
  locale = 'en',
) {
  const text = describeSchedule(schedule, copy, locale);
  return schedule && !enabled ? formatCopy(copy.agentStartsWhenOn, { schedule: text }) : text;
}

/** The IANA zone's place name, as people say it: "Europe/Stockholm" -> "Stockholm". */
export const placeOf = (timezone: string | undefined) =>
  (timezone ?? browserTimezone()).split('/').pop()!.replace(/_/g, ' ');

/** The Turn on confirmation: "Runs weekdays at 08:00 (Stockholm). Turn on?" A custom cron is
 *  never shown to people (DES1 WS-01d): "Runs on a custom schedule (Stockholm). Turn on?" */
export function turnOnQuestion(schedule: AgentSchedule, copy: ScheduleCopy = EN, locale = 'en') {
  const form = scheduleForm(schedule);
  const place = placeOf(schedule.timezone);
  if (form.preset === 'custom') return formatCopy(copy.agentTurnOnCustom, { place });
  const text = describeSchedule(schedule, copy, locale);
  // It reads on after "Runs": lower-case its first letter, unless that is a weekday's own name
  // ("Runs Mondays at 08:00"; "Läuft jeden Montag um 08:00").
  const keepsCapital =
    form.preset === 'weekly' && text.startsWith(weekdayName(form.weekday, locale));
  const phrase = keepsCapital ? text : text.charAt(0).toLocaleLowerCase(locale) + text.slice(1);
  return formatCopy(copy.agentTurnOnQuestion, { schedule: phrase, place });
}

/** "Weekdays at 08:00", "Every hour", "Custom schedule", "Not scheduled": words, never a cron. */
export function describeSchedule(
  schedule: AgentSchedule | null | undefined,
  copy: ScheduleCopy = EN,
  locale = 'en',
) {
  if (!schedule) return copy.scheduleNone;
  const form = scheduleForm(schedule);
  if (form.preset === 'hourly') return copy.scheduleHourly;
  if (form.preset === 'daily') return formatCopy(copy.scheduleDaily, { time: form.time });
  if (form.preset === 'weekdays') return formatCopy(copy.scheduleWeekdays, { time: form.time });
  if (form.preset === 'weekly')
    return formatCopy(copy.scheduleWeekly, {
      weekday: weekdayName(form.weekday, locale),
      time: form.time,
    });
  return copy.scheduleCustom;
}
